import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, FileBarChart, Equal, Lock, DollarSign, Receipt, TrendingUp, TrendingDown, PiggyBank } from 'lucide-react';
import { groupByAccountingClass, CHARGE_CLASSES, PRODUIT_CLASSES } from '../utils/accountingClassification';
import PeriodFilter, { applyPeriodFilter, periodToRange } from '../components/PeriodFilter';
import DownloadFinancePdfButton from '../components/DownloadFinancePdfButton';

const formatCR = (n) => (parseFloat(n) || 0).toLocaleString('fr-FR');

export default function CompteResultatInvestisseur({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState(null);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/dashboard`);
        setFinancialData(res.data);
      } catch (e) {
        console.error(e);
        setFinancialData({ expenses: [], customExpenses: [], credits: [], debits: [] });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading || !financialData) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const allExpenses = [...(financialData.expenses || []), ...(financialData.customExpenses || [])];
  const totalExpenses = allExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
  const credits = applyPeriodFilter(financialData.credits || [], period);
  const debits = applyPeriodFilter(financialData.debits || [], period);

  const produitEntriesForClassif = credits.map((c) => ({
    label: `${c.annonceur || ''} ${c.description || ''}`,
    amount: parseFloat(c.montant) || 0,
    original: c,
  }));
  const chargeEntriesForClassif = debits.length > 0
    ? debits.map((d) => ({
        label: `${d.categorie || ''} ${d.description || ''}`,
        amount: parseFloat(d.montant) || 0,
        original: d,
      }))
    : allExpenses.map((exp) => ({
        label: exp.category || '',
        amount: parseFloat(exp.amount) || 0,
        original: { categorie: exp.category },
      }));

  const produitsGrouped = groupByAccountingClass(produitEntriesForClassif, PRODUIT_CLASSES);
  const chargesGrouped = groupByAccountingClass(chargeEntriesForClassif, CHARGE_CLASSES);
  const totalProduits = produitsGrouped.reduce((s, g) => s + g.total, 0);
  const benefice = totalProduits - totalExpenses;
  const isPositif = benefice >= 0;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="compte-resultat-investisseur">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="gap-2"
              data-testid="cr-inv-back-btn"
            >
              <ArrowLeft className="h-4 w-4" /> Retour
            </Button>
            <DownloadFinancePdfButton
              type="compte-resultat"
              {...periodToRange(period)}
              testId="cr-inv-download-pdf"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <FileBarChart className="h-7 w-7 text-indigo-600" /> Compte de Résultat
              </h1>
              <p className="text-gray-600 mt-1 text-sm">
                {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="gap-1 border-gray-300 text-gray-600 bg-gray-50">
            <Lock className="h-3 w-3" /> Lecture seule
          </Badge>
          <PeriodFilter value={period} onChange={setPeriod} color="indigo" testIdPrefix="cr-period" />
        </div>

        {/* KPI Cards - Charges, Produits, Bénéfice, Part Investisseurs - cliquables */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card
            onClick={() => navigate('/tableau-depenses')}
            className="border-0 shadow-lg bg-gradient-to-br from-red-500 to-red-600 text-white cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-300"
            data-testid="kpi-charges-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Charges totales</p>
                  <p className="text-3xl font-bold mt-2">{formatCR(totalExpenses)}FCFA</p>
                  <p className="text-xs opacity-75 mt-1">→ Voir tableau des dépenses</p>
                </div>
                <Receipt className="h-12 w-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => navigate('/tableau-produits')}
            className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-300"
            data-testid="kpi-ca-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Produits</p>
                  <p className="text-3xl font-bold mt-2">{formatCR(totalProduits)}FCFA</p>
                  <p className="text-xs opacity-75 mt-1">→ Voir tableau comptable</p>
                </div>
                <DollarSign className="h-12 w-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => navigate('/graphique-benefices')}
            className={`border-0 shadow-lg text-white cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-300 ${
              benefice >= 0
                ? 'bg-gradient-to-br from-blue-600 to-blue-700'
                : 'bg-gradient-to-br from-red-700 to-red-800'
            }`}
            data-testid="kpi-benefice-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Résultat</p>
                  <p className="text-3xl font-bold mt-2">{formatCR(benefice)}FCFA</p>
                  <p className="text-xs opacity-75 mt-1">→ Voir graphique évolution</p>
                </div>
                {benefice >= 0 ? (
                  <TrendingUp className="h-12 w-12 opacity-80" />
                ) : (
                  <TrendingDown className="h-12 w-12 opacity-80" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => navigate('/tableau-investisseurs')}
            className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-300"
            data-testid="kpi-investisseurs-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Part Investisseurs (20%)</p>
                  <p className="text-3xl font-bold mt-2">{formatCR(Math.floor(benefice * 0.20))}FCFA</p>
                  <p className="text-xs opacity-75 mt-1">→ Voir tableau investissement</p>
                </div>
                <PiggyBank className="h-12 w-12 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tableau Compte de Résultat (bilatéral Charges | Produits) */}
        <Card className="shadow-lg border-2 border-indigo-100">
          <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <FileBarChart className="h-6 w-6" /> Compte de Résultat
            </CardTitle>
            <PeriodFilter value={period} onChange={setPeriod} color="indigo" testIdPrefix="cr-inv-period" />
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* CHARGES */}
              <div className="border-r border-gray-200">
                <div className="bg-red-600 text-white px-6 py-3">
                  <h3 className="font-bold text-lg uppercase tracking-wide">Charges (Débit)</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-red-50 border-b border-red-200">
                    <tr>
                      <th className="px-6 py-2 text-left text-xs font-semibold text-red-800 uppercase">Libellé</th>
                      <th className="px-6 py-2 text-right text-xs font-semibold text-red-800 uppercase">Montant (FCFA)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {chargesGrouped.length === 0 ? (
                      <tr>
                        <td className="px-6 py-3 text-gray-400 italic">Aucune charge enregistrée</td>
                        <td className="px-6 py-3 text-right text-gray-400">0</td>
                      </tr>
                    ) : (
                      chargesGrouped.map((group) => (
                        <React.Fragment key={`ch-${group.code}`}>
                          <tr className="bg-red-100/70">
                            <td colSpan={2} className="px-4 py-2 text-xs font-bold text-red-900 uppercase">
                              {group.code} - {group.label}
                            </td>
                          </tr>
                          {group.entries.map((e, i) => (
                            <tr key={`ch-${group.code}-${i}`} className="hover:bg-red-50/50">
                              <td className="px-6 py-2 pl-10 text-gray-700 text-sm">
                                {e.original?.categorie || 'Divers'}
                              </td>
                              <td className="px-6 py-2 text-right text-red-700 font-medium">
                                {formatCR(e.amount)}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                    {isPositif && benefice > 0 && (
                      <tr className="bg-blue-50 border-t border-blue-200">
                        <td className="px-6 py-3 italic text-blue-700 font-medium">Résultat (Bénéfice)</td>
                        <td className="px-6 py-3 text-right text-blue-700 font-bold">{formatCR(benefice)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PRODUITS */}
              <div>
                <div className="bg-green-600 text-white px-6 py-3">
                  <h3 className="font-bold text-lg uppercase tracking-wide">Produits (Crédit)</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-green-50 border-b border-green-200">
                    <tr>
                      <th className="px-6 py-2 text-left text-xs font-semibold text-green-800 uppercase">Libellé</th>
                      <th className="px-6 py-2 text-right text-xs font-semibold text-green-800 uppercase">Montant (FCFA)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-green-100">
                    {produitsGrouped.length === 0 ? (
                      <tr>
                        <td className="px-6 py-3 text-gray-400 italic">Aucun produit enregistré</td>
                        <td className="px-6 py-3 text-right text-gray-400">0</td>
                      </tr>
                    ) : (
                      produitsGrouped.map((group) => (
                        <React.Fragment key={`p-${group.code}`}>
                          <tr className="bg-green-100/70">
                            <td colSpan={2} className="px-4 py-2 text-xs font-bold text-green-900 uppercase">
                              {group.code} - {group.label}
                            </td>
                          </tr>
                          {group.entries.map((e, i) => (
                            <tr key={`p-${group.code}-${i}`} className="hover:bg-green-50/50">
                              <td className="px-6 py-2 pl-10 text-gray-700 text-sm">
                                {e.original?.annonceur || 'Divers'}
                              </td>
                              <td className="px-6 py-2 text-right text-green-700 font-medium">
                                {formatCR(e.amount)}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                    {!isPositif && benefice < 0 && (
                      <tr className="bg-orange-50 border-t border-orange-200">
                        <td className="px-6 py-3 italic text-orange-700 font-medium">Résultat (Perte)</td>
                        <td className="px-6 py-3 text-right text-orange-700 font-bold">
                          {formatCR(Math.abs(benefice))}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Ligne totaux - sur la même ligne (responsive) */}
            <div className="grid grid-cols-2 bg-gradient-to-r from-red-100 via-gray-100 to-green-100 border-t-2 border-b-2 border-gray-300 font-bold">
              <div className="px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-2 border-r border-gray-300">
                <span className="text-red-900 uppercase tracking-wide text-xs md:text-sm">Total Charges</span>
                <span className="text-red-900 text-base md:text-xl whitespace-nowrap">
                  {formatCR(totalExpenses + (isPositif ? benefice : 0))} FCFA
                </span>
              </div>
              <div className="px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-2">
                <span className="text-green-900 uppercase tracking-wide text-xs md:text-sm">Total Produits</span>
                <span className="text-green-900 text-base md:text-xl whitespace-nowrap">
                  {formatCR(totalProduits + (!isPositif ? Math.abs(benefice) : 0))} FCFA
                </span>
              </div>
            </div>

            {/* Résultat net */}
            <div
              className={`px-6 py-4 border-t-4 ${
                isPositif ? 'bg-blue-100 border-blue-400' : 'bg-orange-100 border-orange-400'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Equal className="h-5 w-5" />
                  <span className="font-bold text-lg uppercase tracking-wide">
                    Résultat Net {isPositif ? '(Bénéfice)' : '(Perte)'}
                  </span>
                </div>
                <span
                  className={`font-bold text-2xl ${isPositif ? 'text-blue-800' : 'text-orange-800'}`}
                  data-testid="resultat-net-investisseur"
                >
                  {isPositif ? '+' : ''}
                  {formatCR(benefice)} FCFA
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
